/* eslint-disable @typescript-eslint/no-non-null-assertion */
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import React, { useCallback, useState } from "react";

import {
  ChildMixUpdate,
  MixBasket,
  NameBasedSteelGradePlanItem,
} from "src/store/api/generatedApi";

import { useTenantTranslation } from "hooks/formatters";
import { disableEditEventParam, enableEditEventParam } from "./utils";

type SplitDistribution = {
  steelGradeName: string;
  leftHeats: number;
  rightHeats: number;
}[];

interface Props {
  mixId: number;
  name: string;
  description: string | undefined;
  steelGradeProduction: NameBasedSteelGradePlanItem[];
  baskets: MixBasket[];
  doSubmit: (mixUpdates: ChildMixUpdate[], comment: string) => Promise<void>;
  disabled: boolean;
}
interface PopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onConfirm: (updates: ChildMixUpdate[], comment: string) => Promise<void>;
  mixId: number;
  name: string;
  description: string | undefined;
  steelGradeProduction: NameBasedSteelGradePlanItem[];
  baskets: MixBasket[];
  isLoading: boolean;
}
interface ContentProps {
  leftName: string;
  setLeftName: (name: string) => void;
  rightName: string;
  setRightName: (name: string) => void;
  leftDescription: string | undefined;
  setLeftDescription: (description: string | undefined) => void;
  rightDescription: string | undefined;
  setRightDescription: (description: string | undefined) => void;
  comment: string;
  setComment: (comment: string) => void;
  splitDistribution: SplitDistribution;
  setSplitDistribution: (splitDistribution: SplitDistribution) => void;
}

export const SplitMixButton = ({
  mixId,
  name,
  description,
  steelGradeProduction,
  baskets,
  doSubmit,
  disabled,
}: Props) => {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const gridAPI = useGridApiContext();

  const handleConfirm = async (updates: ChildMixUpdate[], comment: string) => {
    setIsSubmitting(true);
    gridAPI.current.publishEvent("stateChange", disableEditEventParam);
    await doSubmit(updates, comment);
    setIsSubmitting(false);
    setAnchorEl(null);
    gridAPI.current.publishEvent("stateChange", enableEditEventParam);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <IconButton
        data-testid="split-mix-button"
        onClick={handleClick}
        disabled={isSubmitting || disabled}
        color="primary"
        size="small"
        sx={{
          maxWidth: "30px",
          maxHeight: "30px",
          minWidth: "30px",
          minHeight: "30px",
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
        }}
      >
        <CallSplitRoundedIcon sx={{ transform: "rotate(90deg)" }} />
      </IconButton>
      <SplitMixPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isLoading={isSubmitting}
        mixId={mixId}
        name={name}
        description={description}
        steelGradeProduction={steelGradeProduction}
        baskets={baskets}
      />
    </Box>
  );
};

const SplitMixPopover = ({
  open,
  anchorEl,
  onClose,
  onConfirm,
  mixId,
  name,
  description,
  steelGradeProduction,
  baskets,
  isLoading,
}: PopoverProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  const [splitDistribution, setSplitDistribution] = useState(
    steelGradeProduction.map(({ steel_grade_name, num_heats }) => ({
      steelGradeName: steel_grade_name,
      leftHeats: num_heats,
      rightHeats: 0,
    }))
  );
  const [leftDescription, setLeftDescription] = useState(description);
  const [rightDescription, setRightDescription] = useState(description);
  const [comment, setComment] = useState("");
  const [leftName, setLeftName] = useState(`${name} (1)`);
  const [rightName, setRightName] = useState(`${name} (2)`);

  const zeroLeftHeats = splitDistribution.every(
    ({ leftHeats }) => leftHeats === 0
  );
  const zeroRightHeats = splitDistribution.every(
    ({ rightHeats }) => rightHeats === 0
  );

  const handleConfirm = useCallback(async () => {
    const mixCreate = { baskets, thresholds: [] };
    const updates: ChildMixUpdate[] = [
      {
        parent_mix_ids: [mixId],
        new_mix_production: {
          name: leftName,
          description: leftDescription,
          production: splitDistribution
            .filter(({ leftHeats }) => leftHeats > 0)
            .map(({ steelGradeName, leftHeats }) => ({
              steel_grade_name: steelGradeName,
              num_heats: leftHeats,
            })),
          mix: mixCreate,
        },
      },
      {
        parent_mix_ids: [mixId],
        new_mix_production: {
          name: rightName,
          description: rightDescription,
          production: splitDistribution
            .filter(({ rightHeats }) => rightHeats > 0)
            .map(({ steelGradeName, rightHeats }) => ({
              steel_grade_name: steelGradeName,
              num_heats: rightHeats,
            })),
          mix: mixCreate,
        },
      },
    ];
    await onConfirm(updates, comment);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- onConfirm is a prop callback with unstable identity; splitDistribution is derived state that changes on every slider interaction
  }, [
    mixId,
    baskets,
    leftName,
    rightName,
    leftDescription,
    rightDescription,
    comment,
  ]);
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            overflowY: "auto",
            border: `1px solid ${theme.palette.secondary.light}`,
            borderRadius: 2,
            minWidth: 500,
            p: 2,
          },
        },
      }}
    >
      <Box sx={{ py: 1, px: 1.5 }}>
        <Typography variant="h3" sx={{ color: "text.secondary" }}>
          {t("splitMix")}
        </Typography>
      </Box>
      <SplitMixContent
        leftName={leftName}
        setLeftName={setLeftName}
        rightName={rightName}
        setRightName={setRightName}
        leftDescription={leftDescription}
        setLeftDescription={setLeftDescription}
        rightDescription={rightDescription}
        setRightDescription={setRightDescription}
        splitDistribution={splitDistribution}
        setSplitDistribution={setSplitDistribution}
        comment={comment}
        setComment={setComment}
      />

      <Box
        sx={{
          p: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          color="secondary"
          variant="outlined"
          onClick={onClose}
          startIcon={<CancelRoundedIcon sx={{ fontSize: 14 }} />}
          size="small"
        >
          {t("cancel")}
        </Button>
        <Button
          data-testid="split-mix-confirm"
          variant="contained"
          onClick={() => void handleConfirm()}
          loading={isLoading}
          disabled={zeroLeftHeats || zeroRightHeats}
          startIcon={
            <CallSplitRoundedIcon sx={{ transform: "rotate(90deg)" }} />
          }
          size="small"
        >
          {t("confirm")}
        </Button>
      </Box>
    </Popover>
  );
};

const SplitMixContent = ({
  leftName,
  setLeftName,
  rightName,
  setRightName,
  leftDescription,
  setLeftDescription,
  rightDescription,
  setRightDescription,
  splitDistribution,
  setSplitDistribution,
  comment,
  setComment,
}: ContentProps) => {
  const { t } = useTenantTranslation();
  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1} justifyContent="space-between">
        <TextField
          label={t("firstMixName")}
          value={leftName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setLeftName(event.target.value);
          }}
          sx={{ width: "48%" }}
        />
        <TextField
          label={t("secondMixName")}
          value={rightName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setRightName(event.target.value);
          }}
          sx={{ width: "48%" }}
        />
      </Stack>
      {splitDistribution.map((split, index) => (
        <SplitRow
          splitDistribution={splitDistribution}
          setSplitDistribution={setSplitDistribution}
          index={index}
          key={`${split.steelGradeName}-split-row`}
        />
      ))}
      <Stack direction="row" gap={1} justifyContent="space-between">
        <TextField
          label={t("description")}
          multiline
          rows={2}
          value={leftDescription}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setLeftDescription(event.target.value);
          }}
          sx={{ width: "48%" }}
        />
        <TextField
          label={t("description")}
          multiline
          rows={2}
          value={rightDescription}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setRightDescription(event.target.value);
          }}
          sx={{ width: "48%" }}
        />
      </Stack>
      <TextField
        label={t("reasonForSplit")}
        value={comment}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setComment(event.target.value);
        }}
      />
    </Stack>
  );
};

const SplitRow = ({
  splitDistribution,
  setSplitDistribution,
  index,
}: {
  splitDistribution: SplitDistribution;
  setSplitDistribution: (splitDistribution: SplitDistribution) => void;
  index: number;
}) => {
  const split = splitDistribution[index]!;
  const { steelGradeName, leftHeats, rightHeats } = split;
  const shiftLeft = () => {
    setSplitDistribution([
      ...splitDistribution.slice(0, index),
      rightHeats <= 0
        ? split
        : {
            steelGradeName,
            leftHeats: leftHeats + 1,
            rightHeats: rightHeats - 1,
          },
      ...(index === splitDistribution.length - 1
        ? []
        : splitDistribution.slice(index + 1)),
    ]);
  };
  const shiftRight = () => {
    setSplitDistribution([
      ...splitDistribution.slice(0, index),
      leftHeats <= 0
        ? split
        : {
            steelGradeName,
            leftHeats: leftHeats - 1,
            rightHeats: rightHeats + 1,
          },
      ...(index === splitDistribution.length - 1
        ? []
        : splitDistribution.slice(index + 1)),
    ]);
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap={2}
      >
        <Typography>{split.steelGradeName}</Typography>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <Typography variant="body1Mono">{split.leftHeats}</Typography>
          <IconButton onClick={shiftLeft} disabled={rightHeats <= 0}>
            <KeyboardDoubleArrowLeftRoundedIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="flex-start">
        <IconButton
          data-testid={`split-mix-shift-right-${index}`}
          onClick={shiftRight}
          disabled={leftHeats <= 0}
        >
          <KeyboardDoubleArrowRightRoundedIcon />
        </IconButton>
        <Typography variant="body1Mono">{split.rightHeats}</Typography>
      </Stack>
    </Box>
  );
};
